/**
 * MoftakiWhatsNewDialog — "MoftakiAI got smarter" v2 announcement.
 * Auto-opens once (localStorage belab.moftaki.whatsNew.v2), lists the
 * assistant's new powers, and offers 6 clickable example prompts that start a
 * message. Reachable any time from the "What's new" link in the chat header.
 */
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material';
import {
  AutoAwesome, Science, RequestQuote, BeachAccess, Feedback as FeedbackIcon,
  Category as CategoryIcon, Download, Psychology,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

export const WHATS_NEW_LS_KEY = 'belab.moftaki.whatsNew.v2';

/** True when the announcement has not been dismissed yet. */
export function shouldShowWhatsNew(): boolean {
  try { return !localStorage.getItem(WHATS_NEW_LS_KEY); } catch { return false; }
}

/** Persist the dismissal (safe against blocked storage). */
export function markWhatsNewSeen(): void {
  try { localStorage.setItem(WHATS_NEW_LS_KEY, '1'); } catch { /* ignore */ }
}

const POWERS: Array<{ icon: React.ReactNode; text: string }> = [
  { icon: <Science sx={{ fontSize: 16 }} />, text: 'Reads every test type — Atterberg to triaxial, all the rock tests, batch PLT & Cerchar' },
  { icon: <RequestQuote sx={{ fontSize: 16 }} />, text: 'Knows the quote pipeline — statuses, aging, a single quote’s full story' },
  { icon: <BeachAccess sx={{ fontSize: 16 }} />, text: 'Sees time off — vacations, sick days and field work, per person and date range' },
  { icon: <FeedbackIcon sx={{ fontSize: 16 }} />, text: 'Tracks feedback tickets, including ones waiting on their submitter' },
  { icon: <CategoryIcon sx={{ fontSize: 16 }} />, text: 'Checks catalogue health — unmapped tests, missing machine time, prices' },
  { icon: <Download sx={{ fontSize: 16 }} />, text: 'Hands you real CSV downloads — bookings, absences, capacity, quotes and more' },
  { icon: <Psychology sx={{ fontSize: 16 }} />, text: 'Shows its live reasoning while it thinks (Claude Sonnet 5)' },
];

const EXAMPLES = [
  'Export everyone on vacation next week',
  'How many quotes are in each pipeline stage right now?',
  'Show me the Atterberg results across all projects',
  'Which feedback tickets are still open?',
  'Which lab tests are missing a catalog mapping?',
  "Export next month's machine bookings to CSV",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onUsePrompt: (text: string) => void;
}

export const MoftakiWhatsNewDialog = ({ open, onClose, onUsePrompt }: Props) => {
  const theme = useTheme();
  const dismiss = () => { markWhatsNewSeen(); onClose(); };
  const use = (t: string) => { onUsePrompt(t); dismiss(); };

  return (
    <Dialog open={open} onClose={dismiss} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pb: 1 }}>
        <AutoAwesome sx={{ color: theme.chat.botAccent }} />
        MoftakiAI got smarter
        <Chip label="NEW" size="small" sx={{
          ml: 0.5, height: 20, fontWeight: 700,
          backgroundColor: alpha(theme.chat.botAccent, 0.12), color: theme.chat.botAccent,
        }} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The assistant now understands the whole lab application — not just Direct Shear:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
          {POWERS.map((p, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ color: theme.palette.primary.main, display: 'flex', mt: 0.2 }}>{p.icon}</Box>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{p.text}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}>
          Try one:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {EXAMPLES.map((t) => (
            <Chip key={t} label={t} onClick={() => use(t)} clickable variant="outlined" color="primary"
              sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.25 } }} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
};
